<template>
  <div class="invite-players-panel" :class="`invite-players-panel--${sport}`">
    <div v-if="embedded" class="panel-toolbar">
      <h3><i class="fa-solid fa-user-plus"></i> Invite players</h3>
      <button type="button" class="btn btn-outline btn-sm" @click="$emit('close')">
        <i class="fa-solid fa-times"></i> Close
      </button>
    </div>

    <div class="invite-content">
      <!-- Search Card -->
      <div class="search-card card slide-in">
        <div class="card-header">
          <div class="card-title">
            <i class="fa-solid fa-search"></i>
            <h2>Search Players</h2>
          </div>
        </div>

        <div class="card-body">
          <!-- Tip -->
          <div class="search-tip">
            <i class="fa-solid fa-lightbulb"></i>
            <p>
              <strong>Tip:</strong> Search by the name on their IPA account, their email, or their customer number.
            </p>
          </div>

          <!-- Search Box -->
          <div class="search-box-wrapper">
            <div class="search-box">
              <i class="fa-solid fa-search"></i>
              <input
                v-model.trim="searchQuery"
                type="text"
                placeholder="Name, email, or customer #..."
                @input="debouncedSearch"
                :disabled="searching"
                class="search-input"
              >
              <button 
                v-if="searchQuery" 
                class="clear-search"
                @click="clearSearch"
                :disabled="searching"
              >
                <i class="fa-solid fa-times"></i>
              </button>
              <div v-if="searching" class="search-spinner">
                <i class="fa-solid fa-spinner fa-spin"></i>
              </div>
            </div>
          </div>

          <!-- Search Results -->
          <div v-if="searchQuery && !searching && searchResults.length > 0" class="search-results">
            <div class="results-header">
              <h3>
                <i class="fa-solid fa-users"></i>
                Found {{ totalResults }} {{ totalResults === 1 ? 'player' : 'players' }}
              </h3>
              <button 
                v-if="hasMoreResults && !showingAll"
                class="btn btn-outline btn-sm"
                @click="loadMoreResults"
                :disabled="loadingMore"
              >
                <i class="fa-solid" :class="loadingMore ? 'fa-spinner fa-spin' : 'fa-chevron-down'"></i>
                Show More Results
              </button>
            </div>

            <div class="results-list">
              <div
                v-for="customer in displayedResults"
                :key="customer.customer_number"
                class="customer-card"
                :class="{ 'selected': isSelected(customer.customer_number) }"
                @click="toggleSelection(customer)"
              >
                <div class="customer-avatar">
                  <i class="fa-solid fa-user"></i>
                </div>
                <div class="customer-info">
                  <h4>{{ customer.full_name }}</h4>
                  <div class="customer-details">
                    <span v-if="customer.email_masked" class="detail-item">
                      <i class="fa-solid fa-envelope"></i>
                      {{ customer.email_masked }}
                    </span>
                    <span class="detail-item detail-item-id">
                      <i class="fa-solid fa-hashtag"></i>
                      {{ customer.customer_number }}
                    </span>
                  </div>
                </div>
                <div class="customer-action">
                  <button 
                    class="btn-icon"
                    :class="isSelected(customer.customer_number) ? 'btn-selected' : 'btn-select'"
                  >
                    <i class="fa-solid" :class="isSelected(customer.customer_number) ? 'fa-check-circle' : 'fa-plus-circle'"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- No Results -->
          <div v-if="searchQuery && !searching && searchResults.length === 0 && hasSearched" class="no-results">
            <div class="no-results-icon">
              <i class="fa-solid fa-user-slash"></i>
            </div>
            <h3>No players found</h3>
            <p>Try a different search term or check the spelling</p>
          </div>

          <!-- Empty State -->
          <div v-if="!searchQuery && !hasSearched" class="empty-search-state">
            <div class="empty-icon">
              <i class="fa-solid fa-search"></i>
            </div>
            <h3>Start searching for players</h3>
            <p>Enter a name, email, or customer number to find and invite them to your team</p>
          </div>
        </div>
      </div>

      <!-- Selected Players Card -->
      <div v-if="selectedPlayers.length > 0" class="selected-card card slide-in" style="animation-delay: 0.1s">
        <div class="card-header">
          <div class="card-title">
            <i class="fa-solid fa-user-check"></i>
            <h2>Selected Players ({{ selectedPlayers.length }})</h2>
          </div>
          <button 
            class="btn btn-outline btn-sm"
            @click="clearSelection"
          >
            <i class="fa-solid fa-times"></i> Clear All
          </button>
        </div>

        <div class="card-body">
          <div class="selected-list">
            <div
              v-for="player in selectedPlayers"
              :key="player.customer_number"
              class="selected-item"
            >
              <div class="selected-avatar">
                <i class="fa-solid fa-user"></i>
              </div>
              <div class="selected-info">
                <h4>{{ player.full_name }}</h4>
                <p v-if="player.email_masked" class="selected-meta">{{ player.email_masked }}</p>
                <p class="selected-meta">#{{ player.customer_number }}</p>
              </div>
              <button 
                class="btn-icon btn-danger"
                @click="removeFromSelection(player.customer_number)"
                title="Remove"
              >
                <i class="fa-solid fa-times"></i>
              </button>
            </div>
          </div>

          <div class="selected-actions">
            <button 
              class="btn btn-primary"
              @click="sendInvites"
              :disabled="sendingInvites"
            >
              <i class="fa-solid" :class="sendingInvites ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
              {{ sendingInvites ? 'Sending Invites...' : `Send Invites (${selectedPlayers.length})` }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import globalToastMixin from "@/mixins/globalToastMixin";
import { buildApiUrl } from "@/config/api";

export default {
  name: "InvitePlayersPanel",
  mixins: [globalToastMixin],
  props: {
    teamId: {
      type: [Number, String],
      required: true,
    },
    sport: {
      type: String,
      default: "football",
      validator: (v) => ["football", "basketball"].includes(v),
    },
    embedded: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["close", "invites-sent"],
  
  data() {
    return {
      searching: false,
      loadingMore: false,
      sendingInvites: false,
      searchQuery: '',
      searchResults: [],
      displayedResults: [],
      totalResults: 0,
      hasMoreResults: false,
      showingAll: false,
      currentOffset: 0,
      searchLimit: 5,
      debounceTimer: null,
      hasSearched: false,
      
      // Selection
      selectedPlayers: [],
    };
  },

  computed: {
    resolvedTeamId() {
      return this.teamId;
    },
    inviteSendUrl() {
      if (this.sport === "basketball") {
        return buildApiUrl(`/api/teams/basketball/invite/send/${this.resolvedTeamId}`);
      }
      return buildApiUrl(`/api/teams/invite/send/${this.resolvedTeamId}`);
    },
    dashboardRouteName() {
      return this.sport === "basketball" ? "BasketballTeamDashboard" : "TeamDashboard";
    },
  },
  
  methods: {
    // ========== HELPER METHODS ==========
    async getAuthToken() {
      return localStorage.getItem('access_token') || 
             sessionStorage.getItem('access_token');
    },
    
    debouncedSearch() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      
      if (!this.searchQuery.trim()) {
        this.clearSearch();
        return;
      }
      
      this.debounceTimer = setTimeout(() => {
        this.performSearch();
      }, 300);
    },
    
    async performSearch() {
      if (!this.searchQuery.trim()) {
        this.clearSearch();
        return;
      }
      
      this.searching = true;
      this.hasSearched = true;
      this.currentOffset = 0;
      this.showingAll = false;
      
      try {
        const results = await this.searchCustomers(this.searchQuery, this.searchLimit, 0);
        
        if (results) {
          this.searchResults = results.customers || [];
          this.displayedResults = [...this.searchResults];
          this.totalResults = results.total || 0;
          this.hasMoreResults = results.has_more || false;
          this.currentOffset = this.searchLimit;
        }
      } catch (error) {
        console.error('Error searching customers:', error);
        this.showToast('error', error.message || 'Failed to search customers');
        this.searchResults = [];
        this.displayedResults = [];
      } finally {
        this.searching = false;
      }
    },
    
    async loadMoreResults() {
      if (this.loadingMore || !this.hasMoreResults) return;
      
      this.loadingMore = true;
      try {
        const results = await this.searchCustomers(this.searchQuery, this.searchLimit, this.currentOffset);
        
        if (results && results.customers) {
          this.searchResults.push(...results.customers);
          this.displayedResults = [...this.searchResults];
          this.hasMoreResults = results.has_more || false;
          this.currentOffset += this.searchLimit;
          
          if (!this.hasMoreResults) {
            this.showingAll = true;
          }
        }
      } catch (error) {
        console.error('Error loading more results:', error);
        this.showToast('error', 'Failed to load more results');
      } finally {
        this.loadingMore = false;
      }
    },
    
    clearSearch() {
      this.searchQuery = '';
      this.searchResults = [];
      this.displayedResults = [];
      this.totalResults = 0;
      this.hasMoreResults = false;
      this.currentOffset = 0;
      this.showingAll = false;
      this.hasSearched = false;
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
    },
    
    // ========== SELECTION METHODS ==========
    isSelected(customerNumber) {
      return this.selectedPlayers.some(p => p.customer_number === customerNumber);
    },
    
    toggleSelection(customer) {
      if (this.isSelected(customer.customer_number)) {
        this.removeFromSelection(customer.customer_number);
      } else {
        this.selectedPlayers.push(customer);
        this.showToast('success', `${customer.full_name} added to selection`);
      }
    },
    
    removeFromSelection(customerNumber) {
      const index = this.selectedPlayers.findIndex(p => p.customer_number === customerNumber);
      if (index !== -1) {
        const removed = this.selectedPlayers.splice(index, 1)[0];
        this.showToast('info', `${removed.full_name} removed from selection`);
      }
    },
    
    clearSelection() {
      this.selectedPlayers = [];
      this.showToast('info', 'Selection cleared');
    },
    // ========== API METHODS ==========
    formatApiErrorDetail(detail) {
      if (detail == null) return "";
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail)) {
        return detail.map((e) => (e && typeof e === "object" && e.msg ? e.msg : String(e))).join("; ");
      }
      if (typeof detail === "object" && detail.msg) return detail.msg;
      try {
        return JSON.stringify(detail);
      } catch {
        return "";
      }
    },

    async searchCustomers(query, limit = 5, offset = 0) {
      const token = await this.getAuthToken();
      if (!token) {
        this.showToast('error', 'Authentication required');
        throw new Error('Authentication required');
      }

      const params = new URLSearchParams({
        q: query.trim(),
        limit: String(limit),
        offset: String(offset),
      });
      const url = buildApiUrl(`/auth/customers/search?${params.toString()}`);

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.showToast('error', 'Session expired. Please login again.');
          this.$router.push({ name: 'CustomerSignIn', query: { redirect: this.$route.fullPath } });
          throw new Error('Session expired');
        }
        const errorData = await response.json().catch(() => ({}));
        const msg = this.formatApiErrorDetail(errorData.detail) || `Search failed: ${response.status}`;
        throw new Error(msg);
      }

      return await response.json();
    },
    
    async sendInvites() {
      if (this.selectedPlayers.length === 0 || !this.resolvedTeamId) {
        this.showToast('warning', 'Please select at least one player to invite');
        return;
      }
      
      this.sendingInvites = true;
      try {
        // Extract only customer numbers and convert to integers
        const customerNumbers = this.selectedPlayers.map(player => {
          const num = parseInt(player.customer_number, 10);
          if (isNaN(num)) {
            throw new Error(`Invalid customer number: ${player.customer_number}`);
          }
          return num;
        });
        
        const result = await this.sendTeamInvites(this.resolvedTeamId, customerNumbers);
        
        if (result) {
          const invitesSent = result.invites_sent || 0;
          const invitesFailed = result.invites_failed || 0;
          
          if (invitesFailed === 0) {
            this.showToast('success', `Successfully sent ${invitesSent} invite(s)`);
          } else {
            this.showToast('warning', 
              `Sent ${invitesSent} invite(s), ${invitesFailed} failed. Check details below.`
            );
            
            if (result.failed_invites && result.failed_invites.length > 0) {
              result.failed_invites.forEach(failed => {
                const playerName = this.selectedPlayers.find(
                  p => p.customer_number === failed.customer_number
                )?.full_name || 'Player';
                this.showToast('error', 
                  `${playerName}: ${failed.reason || 'Failed to send'}`, 
                  'Invite Failed'
                );
              });
            }
          }
          
          this.clearSelection();
          this.clearSearch();
          this.$emit('invites-sent', result);
        }
      } catch (error) {
        console.error('Error sending invites:', error);
        const errorMessage = error.message || 'Failed to send invites. Please try again.';
        this.showToast('error', errorMessage);
      } finally {
        this.sendingInvites = false;
      }
    },
    
    async sendTeamInvites(teamId, customerNumbers) {
      const token = await this.getAuthToken();
      if (!token) {
        this.showToast('error', 'Authentication required');
        throw new Error('Authentication required');
      }
      
      const response = await fetch(this.inviteSendUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ customer_numbers: customerNumbers })
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          this.showToast('error', 'Session expired. Please login again.');
          this.$router.push({ name: 'CustomerSignIn', query: { redirect: this.$route.fullPath } });
          throw new Error('Session expired');
        }
        
        if (response.status === 403) {
          this.showToast('error', 'Only team owner or members can send invites');
          throw new Error('Permission denied');
        }
        
        if (response.status === 404) {
          this.showToast('error', 'Team not found');
          throw new Error('Team not found');
        }
        
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || `Failed to send invites: ${response.status}`;
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      
      // Validate response structure
      if (!result || typeof result.invites_sent === 'undefined') {
        console.warn('Unexpected API response format:', result);
        return {
          invites_sent: customerNumbers.length,
          invites_failed: 0,
          ...result
        };
      }
      
      return result;
    }
  }
};
</script>

<style scoped>
.invite-players-panel {
  margin-bottom: 1.5rem;
}

.panel-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.panel-toolbar h3 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.invite-players-panel--basketball .btn-primary {
  background: linear-gradient(135deg, #e65100, #f57c00);
  border-color: #e65100;
}

/* Main Content */
.invite-content {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;
}

@media (min-width: 1024px) {
  .invite-content {
    grid-template-columns: 1.5fr 1fr;
  }
}

/* Cards */
.card {
  background: var(--background-color2);
  border: 1px solid var(--background-color3);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px var(--box-shadow);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px var(--box-shadow);
}

.card-header {
  padding: 1.5rem;
  border-bottom: 1px solid var(--background-color3);
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, var(--background-color2), var(--background-color1));
}

.card-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.card-title h2 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.card-title i {
  color: var(--border-color-hover);
  font-size: 1.125rem;
}

.card-body {
  padding: 1.5rem;
}

/* Search Tip */
.search-tip {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.search-tip i {
  color: #fbbf24;
  font-size: 1.25rem;
  margin-top: 0.125rem;
  flex-shrink: 0;
}

.search-tip p {
  margin: 0;
  color: var(--text-color1);
  font-size: 0.875rem;
  line-height: 1.6;
}

.search-tip strong {
  color: #fbbf24;
}

/* Search Box */
.search-box-wrapper {
  margin-bottom: 1.5rem;
}

.search-box {
  position: relative;
  width: 100%;
}

.search-box i.fa-search {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color2);
  z-index: 1;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  padding-right: 3rem;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 8px;
  color: var(--text-color1);
  font-size: 0.875rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.search-input:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px var(--border-color-hover1), 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.clear-search {
  position: absolute;
  right: 2.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-color2);
  cursor: pointer;
  padding: 0.25rem;
  transition: color 0.2s ease;
}

.clear-search:hover {
  color: var(--text-color1);
}

.search-spinner {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--border-color-hover);
}

/* Search Results */
.search-results {
  margin-top: 1.5rem;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.results-header h3 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.results-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.customer-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.customer-card:hover {
  border-color: var(--border-color-hover);
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.customer-card.selected {
  border-color: var(--border-color-hover);
  background: rgba(var(--border-color-hover-rgb), 0.05);
}

.customer-avatar {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, var(--background-color2), var(--background-color1));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--border-color-hover);
  font-size: 1.25rem;
  border: 2px solid var(--border-color-hover);
  flex-shrink: 0;
}

.customer-info {
  flex: 1;
  min-width: 0;
}

.customer-info h4 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem;
  color: var(--text-color1);
}

.customer-details {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: var(--text-color2);
}

.detail-item i {
  font-size: 0.6875rem;
}

.detail-item-id {
  opacity: 0.88;
}

.customer-action {
  flex-shrink: 0;
}

.btn-icon {
  padding: 0.5rem;
  background: transparent;
  border: 1px solid var(--background-color3);
  border-radius: 8px;
  color: var(--text-color1);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  min-height: 36px;
}

.btn-select:hover {
  background: var(--background-color3);
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
}

.btn-selected {
  background: rgba(16, 185, 129, 0.1);
  border-color: #10b981;
  color: #10b981;
}

.btn-danger {
  background: rgba(239, 68, 68, 0.1);
  border-color: #ef4444;
  color: #ef4444;
}

.btn-danger:hover {
  background: rgba(239, 68, 68, 0.2);
}

/* No Results / Empty State */
.no-results,
.empty-search-state {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-color2);
}

.no-results-icon,
.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: var(--background-color3);
  opacity: 0.6;
}

.no-results h3,
.empty-search-state h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--text-color1);
}

.no-results p,
.empty-search-state p {
  color: var(--text-color2);
  font-size: 0.9375rem;
}

/* Selected Players */
.selected-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.selected-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 8px;
}

.selected-avatar {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, var(--background-color2), var(--background-color1));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--border-color-hover);
  font-size: 1rem;
  border: 2px solid var(--border-color-hover);
  flex-shrink: 0;
}

.selected-info {
  flex: 1;
  min-width: 0;
}

.selected-info h4 {
  font-size: 0.9375rem;
  font-weight: 600;
  margin: 0 0 0.25rem;
  color: var(--text-color1);
}

.selected-info p {
  font-size: 0.75rem;
  color: var(--text-color2);
  margin: 0;
}

.selected-info .selected-meta + .selected-meta {
  margin-top: 0.15rem;
}

.selected-actions {
  padding-top: 1rem;
  border-top: 1px solid var(--background-color3);
}

/* Buttons */
.btn {
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  min-height: 44px;
}

.btn-primary {
  background: var(--border-color-hover);
  color: var(--button-color);
  border-color: var(--border-color-hover);
}

.btn-primary:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-outline {
  background: transparent;
  color: var(--text-color1);
  border-color: var(--button-border-color);
}

.btn-outline:hover:not(:disabled) {
  background: var(--background-color3);
  border-color: var(--border-color-hover);
}

.btn-sm {
  padding: 0.5rem 1rem;
  font-size: 0.8125rem;
  min-height: 36px;
}

/* Animations */
.slide-in {
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .invite-content {
    gap: 1rem;
  }
  
  .card-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .results-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .customer-card {
    flex-direction: column;
    text-align: center;
  }
  
  .customer-info {
    width: 100%;
  }
}

@media (max-width: 1024px) {
  .invite-content {
    grid-template-columns: 1fr;
  }
}
</style>
